// Flow Builder Page Integration

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  IconButton,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';

import FlowBuilderApp from '../components/flow-builder/FlowBuilderApp.tsx';
import useProject from '../hooks/useProject';
import useAuth from '../hooks/useAuth';

const FlowBuilder = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { project, isLoadingProject, projectError } = useProject({ projectId });
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
  }, [isAuthenticated, navigate]);

  const handleBackToFoundation = () => {
    navigate(`/projects/${projectId}/foundation`);
  };

  if (!isAuthenticated) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isLoadingProject) {
    return (
      <Box sx={{ 
        height: 'calc(100vh - 72px)',
        display: 'flex', 
        flexDirection: 'column',
        mt: -1,
        mx: -1
      }}>
        <Box sx={{ p: 2 }}>
          <IconButton 
            onClick={handleBackToFoundation}
            sx={{ 
              color: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.light',
                color: 'white'
              }
            }}
            size="large"
          >
            <ArrowBackIcon fontSize="large" />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (projectError) {
    return (
      <Box sx={{ 
        height: 'calc(100vh - 72px)',
        display: 'flex', 
        flexDirection: 'column',
        mt: -1,
        mx: -1
      }}>
        <Box sx={{ p: 2 }}>
          <IconButton 
            onClick={handleBackToFoundation}
            sx={{ 
              color: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.light',
                color: 'white'
              }
            }}
            size="large"
          >
            <ArrowBackIcon fontSize="large" />
          </IconButton>
        </Box>
        <Alert severity="error" sx={{ mt: 4, mx: 2 }}>
          {projectError}
        </Alert>
      </Box>
    );
  }

  if (!project) {
    return (
      <Box sx={{ 
        height: 'calc(100vh - 72px)',
        display: 'flex', 
        flexDirection: 'column',
        mt: -1,
        mx: -1
      }}>
        <Box sx={{ p: 2 }}>
          <IconButton 
            onClick={handleBackToFoundation}
            sx={{ 
              color: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.light',
                color: 'white'
              }
            }}
            size="large"
          >
            <ArrowBackIcon fontSize="large" />
          </IconButton>
        </Box>
        <Alert severity="info" sx={{ mt: 4, mx: 2 }}>
          Project not found. Please check the project ID and try again.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      height: 'calc(100vh - 72px)', // Subtract navigation bar height
      display: 'flex', 
      flexDirection: 'column',
      mt: -1, // Compensate for MainLayout padding
      mx: -1  // Remove MainLayout horizontal padding for full width
    }}>
      {/* Flow Builder App with custom styling */}
      <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
        {error ? (
          <Alert severity="error" sx={{ mx: 2 }}>
            {error}
          </Alert>
        ) : (
          <FlowBuilderApp 
            projectId={projectId}
            userId={user?.id}
            onBackToFoundation={handleBackToFoundation}
          />
        )}
      </Box>
    </Box>
  );
};

export default FlowBuilder;